import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import Select from 'react-select';

import NoHoverButton from '../../../shared/components/NoHoverButton';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import { FormGroup } from '../../../shared/components/FormHandler/FormHandler';
import FormLabel from '../../../shared/components/FormLabel/FormLabel';
import generateUniqueId from '../../../shared/utils/generateUniqueId';

import {
  fetchExercises,
  fetchTopPr,
  resetTopPrError,
} from '../ducks/feedActions';
import { feedTracker } from '../../../shared/utils/amplitudeHelper';

const ActionWrapper = styled('div')`
  display: flex;
  justify-content: end;
  align-items: center;
  font-weight: 500;
  font-size: 12px;
  color: #999;
  border-top: 1px solid #f0f0f0;
  border-bottom-right-radius: 50%;
  border-bottom-left-radius: 50%;
  width: 100%;
  z-index: 0;
  bottom: 0;
  background: #ffffff;
  height: 50px;
  .submit-button {
    height: 100%;
    width: 100%;
    appearance: none;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 0;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
    border-bottom-right-radius: 5px;
    border-bottom-left-radius: 5px;
    background: #0dcc8a;
    color: #fff;
    font-size: 14px;
    font-weight: 900;
    padding: 6.5px 25px;
    outline: none !important;
    text-decoration: none;
  }
`;

const CloseButtonContainer = styled('div')`
  display: flex;
  justify-content: end;
  align-items: center;
  background: #ffffff;
  cursor: pointer;
  margin-right: 5px;
`;

const ErrorMessageContainer = styled('div')`
  color: #ff6600;
  background-color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  border-radius: 4px;
  width: 75%;
  margin: 0 auto 10px auto;
  padding: 0 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const ErrorText = styled('div')`
  margin-right: 8px;
  text-align: center;
`;

const FormContainer = styled('div')`
  background-color: #ffffff;
  color: #444444;
  width: 300px;
  text-align: left;
  label {
    margin-bottom: 0;
  }
  .select {
    width: 300px;
    margin: auto;
  }
  .form-group {
    padding: 0;
    margin-bottom: 8px;
  }
`;

const ModalBody = styled('div')`
  background-color: #ffffff;
  padding: 40px 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ModalHeaderDiv = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #ffffff;
  font-weight: 500;
  color: #444444;
  border-bottom: 1px solid #979797;
  width: 100%;
  z-index: 1;
  padding: 10px;
  height: 180px;
  h2 {
    width: 90%;
    margin: auto;
    text-align: center;
    line-height: 40px;
    @media screen and (max-width: 400px) {
      line-height: 36px;
      font-size: 28px;
    }
  }
`;

const ModalHeaderText = styled('h2')`
  width: 90%;
  margin: auto;
  text-align: center;
  line-height: 40px;
  @media screen and (max-width: 400px) {
    line-height: 36px;
    font-size: 28px;
  }
`;

const ModalWrapper = styled('div')`
  display: flex;
  width: 100%;
  max-width: 450px;
  .modal-button {
    align-items: center;
    background: "#ff6600";
  }
`;

const NavigationWrapper = styled('div')`
  display: flex;
  justify-content: end;
  align-items: center;
  background: #ffffff;
  font-weight: 500;
  font-size: 12px;
  color: #616161;
  width: 100%;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
  z-index: 1;
  padding: 10px;
  margin-top: 5px;
  height: 25px;
  top: 0;
  .x-button {
    cursor: pointer;
    margin-right: 5px;
  }
`;

const PrimaryWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 900px;
  border-radius: 5px;
  background: #f5f5f5;
  .text-area {
    border: none;
    padding: 20px 20px 20px;
    outline: none;
    resize: none;
    font-size: 28px;
    height: 200px;
    background: #ffffff;
    border: none;
    box-shadow: none;
    .rc-textarea {
      height: 100%;
      padding: 0px;
    }
  }
`;

const TrophyIconWrapper = styled('div')`
  height: 50px;
  width: 50px;
  border-radius: 50%;
  border: 2px solid #444444;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EXERCISE_CATEGORIES = [
  { value: 'L', label: 'Lift' },
  { value: 'R', label: 'SAQ + C' },
  { value: 'C', label: 'Circuits' },
];

const GENDER_OPTIONS = [
  { value: 0, label: 'All' },
  { value: 1, label: 'Male' },
  { value: 2, label: 'Female' },
  { value: 3, label: 'Undefined' },
];

const AddTopPerformanceModal = ({ onRequestClose }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);

  const dispatch = useDispatch();
  const selectedGroups = useSelector((state) => state.feed.data.selectedGroups);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const exercises = useSelector((state) => state.feed.data.exercises);
  const isLoadingExercises = useSelector(
    (state) => state.feed.ui.isLoadingExercises,
  );
  const isTopPrLoading = useSelector((state) => state.feed.ui.isTopPrLoading);
  const topPrError = useSelector((state) => state.feed.data.topPrError);

  const accountCode = currentUser.accountCode !== undefined && currentUser.accountCode;

  const exerciseOptions = () => {
    if (exercises && Array.isArray(exercises) && exercises.length > 0) {
      return exercises.map((exercise) => ({
        value: exercise.id,
        label: exercise.name,
      }));
    }
    return [];
  };

  const exerciseCategorySelectValue = EXERCISE_CATEGORIES.find(
    (option) => option.value === selectedCategory,
  );

  const genderSelectValue = GENDER_OPTIONS.find(
    (option) => option.value === selectedGender,
  );

  const supportErrorMessage = () => (topPrError === 'No leaderboards found based on selected groups'
    ? 'Leaderboard tracking is not enabled for current group selection'
    : topPrError);

  const handleDismissError = () => {
    dispatch(resetTopPrError());
  };

  const handleAddTopPerformanceWidget = () => {
    const newId = generateUniqueId();

    const values = {
      accountCode,
      id: newId,
      exerciseId: selectedExercise.value,
      exerciseName: selectedExercise.label,
      gender: selectedGender,
      rangeType: 3,
      groups: selectedGroups,
      mustBeInAllGroups: false,
    };
    dispatch(fetchTopPr(values, 'add'));
    feedTracker('TV Mode', 'Top performance widget added');
  };

  const handleCloseModal = () => {
    handleDismissError();
    onRequestClose();
  };

  const handleSelectExercise = (option) => {
    setSelectedExercise(option);
  };

  useEffect(() => {
    if (selectedCategory) {
      handleSelectExercise(null);
      dispatch(fetchExercises(accountCode, selectedCategory));
    }
  }, [selectedCategory]);

  const renderErrorSection = () => {
    if (!topPrError) return null;

    return (
      <ErrorMessageContainer>
        <ErrorText>{supportErrorMessage()}</ErrorText>
      </ErrorMessageContainer>
    );
  };

  const renderFormSection = () => (
    <FormContainer>
      <FormGroup>
        <FormLabel>Select Category</FormLabel>
        <Select
          defaultValue={EXERCISE_CATEGORIES[0].value}
          value={exerciseCategorySelectValue}
          options={EXERCISE_CATEGORIES}
          onChange={(option) => setSelectedCategory(option.value)}
          className='select'
        />
      </FormGroup>
      <FormGroup>
        <FormLabel>Select Exercise</FormLabel>
        <Select
          isLoading={isLoadingExercises}
          isDisabled={!selectedCategory || isLoadingExercises}
          defaultValue={null}
          options={exerciseOptions()}
          value={selectedExercise}
          onChange={(option) => handleSelectExercise(option)}
          isRequired
        />
      </FormGroup>
      <FormGroup>
        <FormLabel>Select Gender</FormLabel>
        <Select
          defaultValue={null}
          value={genderSelectValue}
          options={GENDER_OPTIONS}
          onChange={(option) => setSelectedGender(option.value)}
          className='select'
          isRequired
        />
      </FormGroup>
    </FormContainer>
  );

  const renderHeaderSection = () => (
    <ModalHeaderDiv>
      <TrophyIconWrapper>
        <IcomoonReact
          iconSet={iconSet}
          size={32}
          icon='trophy'
          color='#444444'
        />
      </TrophyIconWrapper>
      <ModalHeaderText>
        Display Today&apos;s Top Performance:
      </ModalHeaderText>
    </ModalHeaderDiv>
  );

  const renderNavigationSection = () => (
    <NavigationWrapper>
      <CloseButtonContainer onClick={handleCloseModal}>
        <IcomoonReact
          iconSet={iconSet}
          size={12}
          icon='remove'
          color='#444444'
        />
      </CloseButtonContainer>
    </NavigationWrapper>
  );

  return (
    <ModalWrapper>
      <PrimaryWrapper>
        {renderNavigationSection()}
        {renderHeaderSection()}
        <ModalBody>
          {renderErrorSection()}
          {renderFormSection()}
        </ModalBody>
        <ActionWrapper>
          <NoHoverButton
            label='Submit'
            cta='Add'
            type='button'
            className='submit-button'
            color='#0DCC8A'
            disabled={
              !selectedCategory
              || !selectedExercise
              || !selectedGender === null
              || isTopPrLoading
            }
            onClick={handleAddTopPerformanceWidget}
          >
            Add
          </NoHoverButton>
        </ActionWrapper>
      </PrimaryWrapper>
    </ModalWrapper>
  );
};

AddTopPerformanceModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
};

export default AddTopPerformanceModal;
